import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Container from 'reactstrap/lib/Container';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { get } from 'lodash-es';

import { useToggle } from 'site-modules/shared/hooks/use-toggle';

import { connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { bindToContent, CmsEntities } from 'client/data/models/cms';
import { getStaticImageUrl } from 'client/utils/image-helpers';

import { TrackingHandler } from 'client/tracking/handler';

import { MpgParams } from 'site-modules/shared/utils/mpg/params';
import { getTypeArticleContentPath } from 'site-modules/editorial/utils/content-path';

import { AspectRatioContainer } from 'site-modules/shared/components/aspect-ratio-container/aspect-ratio-container';
import { ClippedContainer } from 'site-modules/shared/components/clipped-container/clipped-container';
import { YoutubeVideoModalAsync } from 'site-modules/shared/components/youtube-video-modal/youtube-video-modal-async';
import { ResearchEntry } from 'site-modules/editorial/components/type-buying-guide/research-entry/research-entry';
import { RENDER_TYPE } from 'client/site-modules/editorial/utils/type-buying-guide-constants';
import { TypeBuyingGuideMap } from 'client/tracking/maps/editorial/type-buying-guide';

import './type-buying-guide.scss';

const MOBILE_MASK = {
  name: 'hero-mask',
  path: 'M0.685,0.955 C0.807,0.967,0.937,0.993,1,0.995 v-0.995 H0 V1 C0.358,0.933,0.439,0.932,0.685,0.955',
};

const DESKTOP_MASK = {
  name: 'hero-mask',
  path: 'M0.808,0.795 C0.833,0.81,0.913,0.858,1,0.925 V0 H0 V0.925 C0.281,1,0.48,0.878,0.538,0.832 C0.668,0.729,0.731,0.746,0.808,0.795',
};

export function EvBuyingGuideUI({ type, content = null, isMobile = false, params = null }) {
  const [isVideoModalOpen, toggleVideoModal] = useToggle(false);
  const [videoModalData, setVideoModalData] = useState(null);

  const openVideoModal = useCallback(
    videoData => {
      setVideoModalData(videoData);
      toggleVideoModal();
    },
    [toggleVideoModal]
  );

  useEffect(() => {
    TrackingHandler.useMap(TypeBuyingGuideMap);
  }, []);

  if (!content) {
    return null;
  }

  const { title, summary } = content;
  const heroImage = content.metadata('heroImage').value();
  const entries = content.child('main').children();
  const hasResearchByTypeEntry = entries.some(
    entry => entry.metadata('renderType').value() === RENDER_TYPE.RESEARCH_BY_TYPE
  );
  const firstArticleTitle = entries.filter(
    entry => entry.metadata('renderType').value() === RENDER_TYPE.ARTICLES_GRID
  )[0]?.title;

  return (
    <main id="main-content" tabIndex={-1} role="main" className="type-buying-guide">
      <figure className="pos-r hero-content mb-2_5 mb-md-0">
        <ClippedContainer
          mask={isMobile ? MOBILE_MASK : DESKTOP_MASK}
          containerClasses="pos-a w-100 left-0 top-0 hero-bg"
        />
        <Container className="px-1">
          <Row className="mt-3_5">
            <Col xs={12} md={7}>
              <h1 className="heading-1 text-white fw-bolder">{title}</h1>
              <p className="size-16 text-white pb-1_25 pb-md-3_5">{summary}</p>
            </Col>
            <Col xs={12} md={5} className="d-flex align-items-end">
              {heroImage && (
                <AspectRatioContainer aspectRatio="42:23" className="w-100">
                  <img src={getStaticImageUrl(heroImage)} alt="" fetchPriority="high" className="w-100" />
                </AspectRatioContainer>
              )}
            </Col>
          </Row>
        </Container>
      </figure>
      {entries.map((entry, index) => {
        const key = `main-${entry.id}-${index}`;
        return (
          <ResearchEntry
            key={key}
            type={type}
            entry={entry}
            isMobile={isMobile}
            isLazyLoadImages={!!index}
            setVideoModalData={openVideoModal}
            params={params}
            hasResearchByTypeEntry={hasResearchByTypeEntry}
            firstArticleTitle={firstArticleTitle}
          />
        );
      })}
      <YoutubeVideoModalAsync
        onToggleModal={toggleVideoModal}
        isOpen={isVideoModalOpen}
        videoId={get(videoModalData, 'videoId')}
        videoAspectRatio={get(videoModalData, 'videoAspectRatio')}
        creativeId={get(videoModalData, 'creativeId')}
        videoTitle={get(videoModalData, 'videoTitle')}
      />
    </main>
  );
}

EvBuyingGuideUI.propTypes = {
  type: PropTypes.string.isRequired,
  content: CmsEntities.Content,
  params: MpgParams,
  isMobile: PropTypes.bool,
};

const mapStateToProps = state => ({
  isMobile: state.mobile,
});

export const TypeBuyingGuide = connect(mapStateToProps)(
  connectToModel(EvBuyingGuideUI, {
    content: bindToContent(({ type, articleId }) => `${getTypeArticleContentPath(type, articleId)}`),
  })
);
